import {
  KeyboardDateTimePicker,
  KeyboardDateTimePickerProps,
} from '@material-ui/pickers';
import { NullableString } from 'mapistry-shared';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { VisuallyHidden } from '../VisuallyHidden';
import {
  getCommonPickerProps,
  muiPickerCss,
  MuiPickerPropsToOmitOnWrapper,
  PickerInputLabel,
  PickerSubText,
  useOnChangeWrapper,
} from './shared';

type DatetimePickerProps = Omit<
  KeyboardDateTimePickerProps,
  // if a use case for a custom datetime display format or a 12-hour clock comes up, we can expose
  //  `format` and/or `ampm` as props, but for now this ensures consistency
  MuiPickerPropsToOmitOnWrapper | 'format' | 'ampm'
> & {
  subText?: NullableString;
  errorText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (event: { target: { value: any } }) => void;
  name: string; // add name for unique ID
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isCompact?: boolean;
  isSubtextHidden?: boolean;
};

const StyledDatetimePicker = styled(KeyboardDateTimePicker)<{
  $isCompact?: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.white};
  ${muiPickerCss}
  ${({ $isCompact }) =>
    $isCompact &&
    css`
      & .MuiFormControlLabel-root {
        margin-right: 0;
      }

      & .MuiInputBase-root {
        min-width: 10rem;
        padding: 0 0 0 0.5rem;
      }

      & .MuiInputAdornment-positionEnd {
        margin-right: -8px;
        margin-left: -8px;
      }
    `};
`;

export const DATETIME_PICKER_DISPLAY_FORMAT = 'MM/dd/yyyy HH:mm';

export function getDatetimePickerInputId(name: string, uniqueSuffix = '') {
  return `${name}-id-${uniqueSuffix}`;
}

export function DatetimePicker({
  disabled,
  value,
  onChange: onChangeProp,
  label,
  subText,
  error,
  errorText,
  name,
  required,
  isLabelHidden,
  isErrorTextHidden,
  isCompact,
  isSubtextHidden,
  ...rest
}: DatetimePickerProps) {
  const [idRandomify] = useState(uuidv4);
  const pickerInputId = getDatetimePickerInputId(name, idRandomify);
  const onChange = useOnChangeWrapper(onChangeProp, (date) => date); // no transform on the date needed
  return (
    <div>
      <ConditionalWrapper
        condition={!!isLabelHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <PickerInputLabel
          required={required}
          error={error}
          htmlFor={pickerInputId}
          label={label}
        />
      </ConditionalWrapper>
      <StyledDatetimePicker
        disabled={disabled}
        required={required}
        error={error}
        onChange={onChange}
        value={value || null} // an unselected date should have value === null
        format={DATETIME_PICKER_DISPLAY_FORMAT}
        ampm={false}
        initialFocusedDate={new Date()}
        $isCompact={isCompact}
        {...getCommonPickerProps(pickerInputId)}
        {...rest}
      />
      <ConditionalWrapper
        condition={!!isErrorTextHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <PickerSubText
          error={error}
          errorText={errorText}
          subText={subText}
          isSubtextHidden={isSubtextHidden}
        />
      </ConditionalWrapper>
    </div>
  );
}
